import type { CaptionCue } from "../captions";
import {
  defineAudioCatalog,
  type AudioCatalog,
  type AudioSource,
  type AudioTrackId,
  type NonEmptyReadonlyArray
} from "../catalog";
import {
  CURRICULUM_VOICE_TRACKS,
  type CurriculumVoiceTrackId
} from "../curriculum-catalog";
import { AudioRuntime, type AudioRuntimeOptions } from "../runtime";

type LocalAudioChannel = "music" | "voice" | "sfx";

function localSources(
  channel: LocalAudioChannel,
  fileName: string
): NonEmptyReadonlyArray<AudioSource> {
  return [
    {
      src: `/audio/${channel}/${fileName}.ogg`,
      type: "audio/ogg"
    },
    {
      src: `/audio/${channel}/${fileName}.mp3`,
      type: "audio/mpeg"
    }
  ];
}

function singleCaption(
  text: string,
  endMs: number
): NonEmptyReadonlyArray<CaptionCue> {
  return [{ startMs: 0, endMs, text }];
}

function voiceTrack(
  fileName: string,
  fallbackCaption: string,
  captions: NonEmptyReadonlyArray<CaptionCue>
) {
  return {
    sources: localSources("voice", fileName),
    gain: 1,
    captions,
    fallbackCaption
  };
}

export const APP_AUDIO_CATALOG = defineAudioCatalog({
  music: {
    map: {
      sources: localSources("music", "map"),
      gain: 0.52,
      loop: true
    },
    chinese: {
      sources: localSources("music", "chinese"),
      gain: 0.48,
      loop: true
    },
    math: {
      sources: localSources("music", "math"),
      gain: 0.46,
      loop: true
    },
    english: {
      sources: localSources("music", "english"),
      gain: 0.48,
      loop: true
    },
    habits: {
      sources: localSources("music", "habits"),
      gain: 0.46,
      loop: true
    },
    "focus-social": {
      sources: localSources("music", "focus-social"),
      gain: 0.44,
      loop: true
    },
    celebration: {
      sources: localSources("music", "celebration"),
      gain: 0.56,
      loop: false
    }
  },
  voice: {
    welcome: voiceTrack(
      "welcome",
      "欢迎来到小小启航岛！点点在码头等你呢，我们一起出发吧。",
      [
        {
          startMs: 0,
          endMs: 2_300,
          text: "欢迎来到小小启航岛！"
        },
        {
          startMs: 2_300,
          endMs: 4_150,
          text: "点点在码头等你呢，"
        },
        {
          startMs: 4_150,
          endMs: 6_100,
          text: "我们一起出发吧。"
        }
      ]
    ),
    choose_profile: voiceTrack(
      "choose_profile",
      "今天哪位小船员来开船？选好就出发。",
      singleCaption("今天哪位小船员来开船？选好就出发。", 4_700)
    ),
    choose_island: voiceTrack(
      "choose_island",
      "五座小岛都亮着灯呢。挑一座喜欢的，我们去看看！",
      [
        { startMs: 0, endMs: 2_300, text: "五座小岛都亮着灯呢。" },
        {
          startMs: 2_300,
          endMs: 5_100,
          text: "挑一座喜欢的，我们去看看！"
        }
      ]
    ),
    listen: voiceTrack(
      "listen",
      "小耳朵准备好。听一听，再找出和声音一样的答案。",
      singleCaption("小耳朵准备好。听一听，再找出和声音一样的答案。", 5_600)
    ),
    arrange: voiceTrack(
      "arrange",
      "小故事讲完啦。想一想，把卡片送回故事里的位置。",
      singleCaption("小故事讲完啦。想一想，把卡片送回故事里的位置。", 5_700)
    ),
    place: voiceTrack(
      "place",
      "先选一个小物品，再帮它找到该去的地方。",
      singleCaption("先选一个小物品，再帮它找到该去的地方。", 4_300)
    ),
    retry: voiceTrack(
      "retry",
      "嗯，就差一点点。我们换个办法，再试一次。",
      singleCaption("嗯，就差一点点。我们换个办法，再试一次。", 5_200)
    ),
    hint: voiceTrack(
      "hint",
      "来看看，点点把小提示亮起来啦。",
      singleCaption("来看看，点点把小提示亮起来啦。", 3_592)
    ),
    success: voiceTrack(
      "success",
      "找到啦！这一站稳稳到达。我们去看看下一站吧。",
      singleCaption("找到啦！这一站稳稳到达。我们去看看下一站吧。", 5_200)
    ),
    rest: voiceTrack(
      "rest",
      "今天的航线走完啦。让小眼睛休息一会儿，明天再见。",
      singleCaption("今天的航线走完啦。让小眼睛休息一会儿，明天再见。", 5_400)
    ),
    ...CURRICULUM_VOICE_TRACKS
  } as AudioCatalog["voice"],
  sfx: {
    tap: {
      sources: localSources("sfx", "tap"),
      gain: 0.44
    },
    hint: {
      sources: localSources("sfx", "hint"),
      gain: 0.58
    },
    success: {
      sources: localSources("sfx", "success"),
      gain: 0.66
    },
    stamp: {
      sources: localSources("sfx", "stamp"),
      gain: 0.62
    },
    rest: {
      sources: localSources("sfx", "rest"),
      gain: 0.56
    }
  }
});

export type AppMusicTrackId = AudioTrackId<
  typeof APP_AUDIO_CATALOG,
  "music"
>;
export const APP_FIXED_VOICE_TRACK_IDS = [
  "welcome",
  "choose_profile",
  "choose_island",
  "listen",
  "arrange",
  "place",
  "retry",
  "hint",
  "success",
  "rest"
] as const;

export type AppFixedVoiceTrackId =
  (typeof APP_FIXED_VOICE_TRACK_IDS)[number];
export type AppVoiceTrackId = AppFixedVoiceTrackId | CurriculumVoiceTrackId;
export type AppSfxTrackId = AudioTrackId<typeof APP_AUDIO_CATALOG, "sfx">;

export function createAppAudioRuntime(
  options: AudioRuntimeOptions = {}
): AudioRuntime {
  return new AudioRuntime(APP_AUDIO_CATALOG, {
    duckingFactor: 0.28,
    ...options
  });
}
